/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { AppBar, Box, Typography, useMediaQuery } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import { Link, matchPath } from 'react-router-dom';
import VisibilityIcon from '@material-ui/icons/Visibility';
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Trans, useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Fab from '@material-ui/core/Fab';
import MenuIcon from '@material-ui/icons/Menu';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { get } from 'lodash';
import CreditCard from '@material-ui/icons/CreditCard';
import { useLocation } from 'react-use';
import { useSelector } from 'react-redux';
import Button from '../common/Button';
import Tooltip from '../common/Tooltip';
import SubmissionStatusChangeDialog from '../status/SubmissionStatusChangeDialog';
import useDraftFooter from './useDraftFooter';
import { uiSchemaProperties } from '../../shared/enums/uiSchemaProperties';
import PaymentDialog from '../../payment/components/dialog/PaymentDialog';
import SignIcon from '../icons/SignIcon';
import { RoutePath } from '../../shared/enums/RoutePath';
import { selectAttachmentsAreLoading } from '../../redux/features/submission/submission.selectors';

const useStyles = makeStyles(theme => ({
  actionButton: {
    margin: '.25rem .75rem',
  },
  linkbutton: {
    marginTop: '2.5rem',
    display: 'flex',
    textDecoration: 'none',
    color: theme.palette.linkColour,
    '& span': {
      fontWeight: 600,
    },
  },
  VisibilityIcon: {
    marginRight: '0.5rem',
  },
  bottomBar: {
    padding: '.25rem',
    top: 'unset',
    bottom: 0,
    left: 0,
    right: 0,
    justifyContent: 'center',
    color: theme.palette.footerText,
    backgroundColor: theme.palette.footerBackground,
    opacity: 1,
    [theme.breakpoints.between('xs', 'md')]: {
      width: '100%',
    },
    [theme.breakpoints.between('md', 'lg')]: {
      width: '100%',
    },
    [theme.breakpoints.between('lg', 'xl2')]: {
      width: '100%',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '100%',
      paddingLeft: '3.5%',
      paddingRight: '3.5%',
    },
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toolbarFirstRow: {
    display: 'flex',
    flexGrow: 50,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  toolbarSecondRow: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexGrow: 0,
    },
  },
  tooltip: {
    alignItems: 'center',
  },
  tooltipWide: {
    maxWidth: '28rem',
    padding: '1rem',
  },
  payButton: {
    margin: '.25rem .75rem',
    backgroundColor: '#066AE3 !important',
    color: '#FFF',
    '&[disabled]': {
      backgroundColor: '#BBDEFB !important',
    },
  },
  fab: {
    position: 'absolute',
    top: '-3.5rem',
    right: 0,
    marginRight: '1.5rem',
    backgroundColor: theme.palette[theme.fabColor].main,
    color: theme.palette[theme.fabColor].contrastText,
    '&[data-focustype="key"]': {
      outline: 'none',
      boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${theme.palette.focus}`,
    },
    '&:focus, &:active': {
      backgroundColor: theme.palette[theme.fabColor].active,
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette[theme.fabColor].hover,
    },
  },
  footerStatusArea: {
    marginRight: 'auto',
    display: 'inline-flex',
    '& > svg': {
      marginRight: theme.spacing(1),
    },
  },
}));

export default function DraftFooter({
  submissionId,
  submission,
  isValid,
  onSave,
  onSaveAndClose,
  onClose,
  onSendToPackage,
  isDialogOpen,
  closeDialog,
  confirmDialog,
  readOnly,
  errors,
  toggleDrawer,
  uiSchema,
  onPay,
  onSendWithoutPayment,
  isPaymentPage = () => false,
  setNonProviderPayment,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const classes = useStyles();
  const isLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const attachmentsAreLoading = useSelector(selectAttachmentsAreLoading);
  const saveDisabled =
    get(uiSchema, `${uiSchemaProperties.UI_ACTIONBAR}.${uiSchemaProperties.UI_DISABLED}`) || attachmentsAreLoading;
  const isSigningPage = () => !!matchPath(location?.pathname, RoutePath.PROCEDURE_SIGNING_SUBMISSION.path);

  const enableApplicantApproval = get(
    uiSchema,
    `${uiSchemaProperties.UI_APPLICANT_APRROVAL}.${uiSchemaProperties.UI_ENABLED}`,
  );

  function getApplicantEmails() {
    // Check if the required fields exist in the submission object
    if (submission && submission.parties && submission.parties.applicants) {
      // Use map to iterate over the applicants and extract the email address
      return submission?.parties?.applicants.map(applicant => applicant?.contactDetails?.email);
    }
    // If the required fields do not exist, return an empty array
    return [];
  }

  // This configuration handles a unique scenario specific to the Benelux countries.
  // In this case, they lack an external provider but still offer the "Pay online" option.
  // Our task is to ensure payment confirmation without involving an external provider.
  // The new button should essentially mimic the "Finish Payment" button but with a distinct label.
  const finishPaymentLabel = get(uiSchema, `fees.${uiSchemaProperties.UI_FINISH_PAYMENT_LABEL}`);
  const forceFinishPayment = get(uiSchema, `fees.${uiSchemaProperties.UI_SHOW_FINISH_PAYMENT}`);
  const isSendWithoutPaymentButtonHidden = get(
    uiSchema,
    `fees.feesSelection.${uiSchemaProperties.UI_HIDE_SEND_WITHOUT_PAYMENT_BUTTON}`,
  );

  const {
    isEditable,
    isToSign,
    isBackToDraft,
    isBackToSign,
    isToSend,
    dialogStatus,
    closeDialogStatus,
    revertStatusAction,
    handleEditSubmissionClick,
    handleBackDraftClick,
    handleBackSignClick,
    handlePreviewClick,
    procedure,
    canPayButtonEnabled,
    canSendWithoutPayingButtonEnabled,
    isExternalIntegrationPayment,
  } = useDraftFooter(onClose, submissionId, submission);

  function isSubmissionInvalid() {
    return !isValid || errors?.length > 0;
  }

  const ReadonlyToolbarActions = () => (
    <>
      <div className={classes.toolbarFirstRow}>
        <Button id="goBack" color="secondary" onClick={onClose} className={classes.actionButton}>
          {t('Back')}
        </Button>
      </div>
      <div className={classes.toolbarSecondRow}>
        {isBackToDraft() === true && (
          <Button id="goBackDraft" color="primary" onClick={handleBackDraftClick} className={classes.actionButton}>
            {t('back.to.draft')}
          </Button>
        )}
        {isBackToSign() === true && (
          <Button id="goBackSign" color="primary" onClick={handleBackSignClick} className={classes.actionButton}>
            {t('back.to.ready.to.sign')}
          </Button>
        )}
        {isToSend() === true && (
          <Button id="send" color="primary" onClick={handlePreviewClick} className={classes.actionButton}>
            {t('send.submission')}
          </Button>
        )}
        {isToSign() === true && (
          <Button id="sign" color="primary" onClick={handlePreviewClick} className={classes.actionButton}>
            {t('submission.sign')}
          </Button>
        )}
        {isEditable() === true && (
          <Button id="edit" color="secondary" onClick={handleEditSubmissionClick} className={classes.actionButton}>
            {t('actions.submission.edit')}
          </Button>
        )}
        {submission && (
          <SubmissionStatusChangeDialog
            open={dialogStatus}
            handleCancel={closeDialogStatus}
            handleConfirm={closeDialogStatus}
            submission={submission}
            revertStatusAction={revertStatusAction}
          />
        )}
      </div>
    </>
  );

  const ToolbarActions = () => {
    if (readOnly) return <ReadonlyToolbarActions />;

    return (
      <>
        <div className={classes.toolbarFirstRow}>
          <Typography id="footerStatusAreaLabel" className={clsx(classes.footerStatusArea, classes.actionButton)}>
            {/* keep this for autosaving indicator */}
          </Typography>

          {isPaymentPage() && canSendWithoutPayingButtonEnabled() && (
            <span>
              <Tooltip
                title={t('footer.submission.payment.tooltip')}
                placement="top"
                className={classes.tooltip}
                customWide={classes.tooltipWide}
              />
            </span>
          )}

          {isPaymentPage() && !isSendWithoutPaymentButtonHidden && (
            <span>
              <Button
                id="sendToSign"
                color="primary"
                className={classes.payButton}
                startIcon={<CreditCard />}
                onClick={onSendWithoutPayment}
                disabled={saveDisabled || !canSendWithoutPayingButtonEnabled() || isSubmissionInvalid()}
              >
                {t('actions.submission.send.without.payment')}
              </Button>
            </span>
          )}

          <Button id="save" color="secondary" disabled={saveDisabled} className={classes.actionButton} onClick={onSave}>
            {t('actions.save')}
          </Button>
        </div>
        <div className={classes.toolbarSecondRow}>
          {!RoutePath.isUmaRoute(location?.pathname) && (
            <Button
              id="saveAndClose"
              color="secondary"
              disabled={saveDisabled}
              className={classes.actionButton}
              onClick={onSaveAndClose}
            >
              {t('actions.save-and-close')}
            </Button>
          )}
          {isSubmissionInvalid() && !isPaymentPage() && !RoutePath.isUmaRoute(location?.pathname) && (
            <Tooltip title={t('messages.submission.not.completed')} wide>
              <span>
                <Button
                  id="sendToSign"
                  color="primary"
                  className={classes.actionButton}
                  startIcon={<SignIcon />}
                  onClick={onSendToPackage}
                  disabled={saveDisabled || isSubmissionInvalid()}
                >
                  {!isSigningPage() ? t('send.to.sign') : t('confirm.signatures')}
                </Button>
              </span>
            </Tooltip>
          )}
          {!isSubmissionInvalid() && !isPaymentPage() && !RoutePath.isUmaRoute(location?.pathname) && (
            <span>
              <Button
                id="sendToSign"
                color="primary"
                className={classes.actionButton}
                startIcon={<SignIcon />}
                onClick={onSendToPackage}
                disabled={saveDisabled || isSubmissionInvalid()}
              >
                {!isSigningPage() ? t('send.to.sign') : t('confirm.signatures')}
              </Button>
            </span>
          )}
          {isPaymentPage() && isExternalIntegrationPayment() && !forceFinishPayment && (
            <span>
              <Button
                id="pay-button"
                color="primary"
                className={classes.payButton}
                startIcon={<CreditCard />}
                onClick={onPay}
                disabled={saveDisabled || !canPayButtonEnabled() || isSubmissionInvalid()}
              >
                {t('actions.submission.proceed')}
              </Button>
            </span>
          )}
          {isPaymentPage() && (!isExternalIntegrationPayment() || forceFinishPayment) && (
            <span>
              <Button
                id="pay-button"
                color="primary"
                className={classes.payButton}
                startIcon={<CreditCard />}
                onClick={setNonProviderPayment}
                disabled={saveDisabled || !canPayButtonEnabled() || isSubmissionInvalid()}
              >
                {t(finishPaymentLabel ?? 'actions.submissions.finish.payment')}
              </Button>
            </span>
          )}
        </div>
      </>
    );
  };

  const ApplicanatDialogActionDefault = () => {
    return (
      <Button color="primary" id="confirm" onClick={confirmDialog}>
        {t('actions.confirm')}
      </Button>
    );
  };

  const ApplicanatDialogActionSendEmails = () => {
    return (
      <Button color="primary" id="confirm" onClick={confirmDialog}>
        {t('actions.status.send.emails.request')}
      </Button>
    );
  };

  const SendToSignDialogDefault = () => {
    return (
      <Dialog open={isDialogOpen} fullWidth maxWidth="md">
        <DialogTitle>
          <Typography component="span" variant="h2">
            {t('actions.status.change')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Trans
            i18nKey="messages.submission.status.change"
            values={{
              fromStatus: t('submission.status.draft'),
              toStatus: t('submission.status.ready.to.sign'),
            }}
          />
          {/* <Box display="flex"> */}
          <Link to={`/${procedure?.id}/preview/${submissionId}`} className={classes.linkbutton}>
            <VisibilityIcon className={classes.VisibilityIcon} />
            <Typography variant="subtitle1" component="span">
              {t('submission.preview')}
            </Typography>
          </Link>
          {/* </Box> */}
        </DialogContent>
        <DialogActions>
          <Button color="default" id="cancel" onClick={closeDialog}>
            {t('actions.cancel')}
          </Button>
          <Button color="primary" id="confirm" onClick={confirmDialog}>
            {t('actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // applicant approval dialog to send emails for applicants to sign
  const ApplicantApprovalDialog = () => {
    const emails = getApplicantEmails(submission);
    const representativesExist =
      submission?.parties?.representatives && submission?.parties?.representatives.length > 0;
    return (
      <Dialog open={isDialogOpen} fullWidth maxWidth="md">
        <DialogTitle>
          <Typography component="span" variant="h2">
            {t('dialog.applicant.approval.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Trans
            i18nKey="messages.submission.status.change"
            values={{
              fromStatus: t('submission.status.draft'),
              toStatus: t('submission.status.ready.to.sign'),
            }}
          />
          {!representativesExist && emails.length > 0 && (
            <>
              <Box className={classes.emailsTitle}>
                <Typography variant="subtitle1" component="span">
                  {t('messages.submission.send.applicants.emails')}
                </Typography>
              </Box>
              <ol>
                {emails.map((email, index) => (
                  <li key={index} className={classes.emailsList}>
                    {email}
                  </li>
                ))}
              </ol>
            </>
          )}
          <Link to={`/${procedure?.id}/preview/${submissionId}`} className={classes.linkbutton}>
            <VisibilityIcon className={classes.VisibilityIcon} />
            <Typography variant="subtitle1" component="span">
              {t('submission.preview')}
            </Typography>
          </Link>
        </DialogContent>
        <DialogActions>
          <Button color="default" id="cancel" onClick={closeDialog}>
            {t('actions.cancel')}
          </Button>
          {representativesExist ? <ApplicanatDialogActionDefault /> : <ApplicanatDialogActionSendEmails />}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <AppBar component="div" position="sticky" elevation={0} className={classes.bottomBar}>
      {!isLargeOrBigger && (
        <Fab size="medium" className={classes.fab} onClick={toggleDrawer} aria-label={t('aria.drawer.open')}>
          <MenuIcon />
        </Fab>
      )}

      <div className={classes.toolbar}>
        <ToolbarActions />
      </div>

      {isPaymentPage() && <PaymentDialog submission={submission} />}
      {/* Check if applicant Approval property enabled in uischema for the siging  */}
      {enableApplicantApproval ? <ApplicantApprovalDialog /> : <SendToSignDialogDefault />}
    </AppBar>
  );
}
